import { config } from '@/config'

/**
 * Session de la SPA — authentification **intégrée** : un **access token court** (~15 min)
 * + un **refresh token opaque** rotatif, émis par l'API.
 *
 * Le renouvellement passe par une **promesse partagée** : la rotation n'accepte qu'un
 * renouvellement à la fois (un jeton tourné ne ressert pas), or un chargement de page
 * part avec plusieurs requêtes de front — sans sérialisation, elles se voleraient le
 * refresh token.
 *
 * Les jetons vivent en `sessionStorage` : durée de vie alignée sur l'onglet, pas de
 * cookie lisible par JS (aucun `HttpOnly` possible ici, le client doit lire le jeton).
 */

const STORAGE_KEY = 'suivipma-session'

/** Marge (ms) : un access token qui expire dans moins que cela est traité comme expiré. */
const EXPIRY_SKEW_MS = 30_000

export interface LocalSession {
  accessToken: string
  refreshToken: string
  /** Échéance de l'access token (epoch ms), dérivée d'`expiresIn` à la réception. */
  expiresAt: number
}

export interface SessionGrant {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export function getLocalSession(): LocalSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LocalSession>
    if (!parsed.accessToken || !parsed.refreshToken || !parsed.expiresAt) return null
    return parsed as LocalSession
  } catch {
    return null
  }
}

/** Mémorise la session à partir de la réponse d'ouverture (inscription, connexion, refresh). */
export function setLocalSession(input: SessionGrant): void {
  const session: LocalSession = {
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    expiresAt: Date.now() + input.expiresIn * 1000,
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    /* stockage indisponible : la session ne survivra pas au rechargement */
  }
}

/**
 * Remplace le seul access token (montée d'assurance après un second facteur) : le refresh
 * token — dont la famille vient d'être montée côté serveur — ne change pas.
 */
export function updateLocalAccessToken(accessToken: string, expiresIn: number): void {
  const current = getLocalSession()
  if (!current) return
  setLocalSession({ accessToken, refreshToken: current.refreshToken, expiresIn })
}

export function clearLocalSession(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* rien à purger */
  }
}

export function isAccessTokenFresh(session: LocalSession): boolean {
  return session.expiresAt - Date.now() > EXPIRY_SKEW_MS
}

/** Vrai si une session exploitable existe : la présence du **refresh token** fait foi. */
export function hasSession(): boolean {
  return getLocalSession() !== null
}

/**
 * Renouvellement en cours, **partagé**. `fetch` brut plutôt que le client `@/lib/api` :
 * le middleware de ce client appelle précisément ce module, l'y faire repasser bouclerait.
 */
let pendingRenewal: Promise<LocalSession | null> | null = null

export function renewLocalSession(): Promise<LocalSession | null> {
  pendingRenewal ??= renew().finally(() => {
    pendingRenewal = null
  })
  return pendingRenewal
}

async function renew(): Promise<LocalSession | null> {
  const current = getLocalSession()
  if (!current) return null
  try {
    const response = await fetch(`${config.apiOrigin}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    })
    if (!response.ok) {
      // 401 = session révoquée ou expirée côté serveur : elle est morte, on purge.
      if (response.status === 401) clearLocalSession()
      return null
    }
    const data = (await response.json()) as SessionGrant
    setLocalSession(data)
    return getLocalSession()
  } catch {
    // Réseau indisponible : la session n'est pas morte pour autant — on ne purge pas.
    return null
  }
}

/**
 * Access token prêt à poser sur la requête. Renouvelle **de façon sérialisée** s'il
 * expire, et rend `null` si le renouvellement échoue (session morte).
 */
export async function getAccessToken(): Promise<string | null> {
  const session = getLocalSession()
  if (!session) return null
  if (isAccessTokenFresh(session)) return session.accessToken
  const renewed = await renewLocalSession()
  return renewed?.accessToken ?? null
}

/**
 * Déconnexion côté serveur (révoque la famille de refresh tokens de cet appareil).
 * `keepalive` : l'appel part souvent juste avant une navigation. Meilleure volonté —
 * l'échec ne bloque jamais la déconnexion locale.
 */
export async function revokeLocalSession(): Promise<void> {
  const session = getLocalSession()
  if (!session) return
  try {
    await fetch(`${config.apiOrigin}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
      keepalive: true,
    })
  } catch {
    /* la révocation serveur attendra l'expiration ; la purge locale, elle, a lieu */
  }
}
